import { useId, useState } from 'react';
import { ChevronDown, ShieldOff } from 'lucide-react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Collapsible, CollapsibleContent } from '@voll-intelligence/ui-kit/ui/collapsible';
import { RadioGroup, RadioGroupItem } from '@voll-intelligence/ui-kit/ui/radio-group';
import { rateTypeKey, type HotelRateTypeKey, type HotelRateView } from '../models.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';
import { CHIP, CHIP_TONE, RateBenefitChips } from './rate_chips.js';

/**
 * O painel do card de quarto: **uma caixa por TIPO de tarifa (café/reembolso),
 * com o fornecedor em cada linha** — a pergunta do viajante é "quero café e
 * poder cancelar?", e só depois "com quem?".
 *
 * Canal não herda benefício de canal (Booking e Expedia vendem o mesmo quarto
 * por contas próprias), então cada linha imprime a própria diária e a comparação
 * é entre números inteiros — nunca a escada de deltas do aéreo. A moldura é do
 * TIPO, não da linha: dentro dela os fornecedores competem pelo mesmo pacote.
 * A negociada é a exceção — caixa própria pinada no topo (é a única que o
 * acordo corporativo cobre); pode haver mais de um acordo (fornecedores
 * distintos), então o fornecedor identifica CADA linha aqui também.
 */
type RateGroup = {
  key: string;
  label: string;
  negotiated: boolean;
  /** 0 = negociada pinada; o resto é a régua de `TYPE_WEIGHT`. */
  weight: number;
  rates: HotelRateView[];
};

/** Do mais completo pro mais nu; o desconhecido fecha a lista. */
const TYPE_WEIGHT: Record<HotelRateTypeKey, number> = {
  BREAKFAST_REFUNDABLE: 1,
  BREAKFAST: 2,
  REFUNDABLE: 3,
  ROOM_ONLY: 4,
  UNSPECIFIED: 5,
};

/** Dentro da caixa, a mais barata primeiro (#6914): a primeira linha do grupo
 *  é a que ele defende. Empate de preço mantém a ordem de chegada. */
function orderedGroupRates(rates: readonly HotelRateView[]): HotelRateView[] {
  const amount = (rate: HotelRateView) => rate.total?.amount ?? Number.POSITIVE_INFINITY;
  return [...rates].sort((a, b) => amount(a) - amount(b));
}

function groupRates(rates: readonly HotelRateView[], copy: HotelRoomsCopy): RateGroup[] {
  const groups = new Map<string, RateGroup>();
  rates.forEach((rate) => {
    const typeKey = rateTypeKey(rate);
    const key = rate.negotiated ? 'negotiated' : `type:${typeKey}`;
    const current = groups.get(key);
    if (current) {
      current.rates.push(rate);
      return;
    }
    groups.set(key, {
      key,
      label: rate.negotiated ? copy.negotiatedLabel : copy.rateTypeLabels[typeKey],
      negotiated: rate.negotiated === true,
      weight: rate.negotiated ? 0 : TYPE_WEIGHT[typeKey],
      rates: [rate],
    });
  });
  return [...groups.values()]
    .map((group) => ({ ...group, rates: orderedGroupRates(group.rates) }))
    .sort((a, b) => a.weight - b.weight);
}

/** Cancelamento não vira chip por linha quando é a MESMA garantia em todas — a
 *  frase sobe pro topo do painel, dita uma vez. Com prazos diferentes por
 *  tarifa, cada linha mantém o seu (ver o chip neutro em `RateLine`). */
function hoistedCancellation(rates: readonly HotelRateView[]): string | undefined {
  const [first] = rates;
  if (!first?.cancellationLabel) return undefined;
  return rates.every((rate) => rate.cancellationLabel === first.cancellationLabel)
    ? first.cancellationLabel
    : undefined;
}

function RateLinePrice({ rate }: { readonly rate: HotelRateView }) {
  /* A linha imprime a diária compacta (`R$ 627/ noite`) com o total da estadia
     embaixo — nunca o par unidade+noites do trilho, que é o preço do CARD:
     repetir a forma dele aqui fazia a linha competir com o número que resume. */
  const nightly = rate.nightlyLabel ?? rate.nightlyPriceLabel;
  const secondary = nightly ? rate.totalLabel : '';
  return (
    <span className="grid shrink-0 justify-items-end @max-md:basis-full">
      {/* `text-title` e não `text-ui`: o preço é o termo comparado da linha.
          Não sobe pra `text-heading` — esse é o preço do CARD. */}
      <span className="text-title tabular-nums">
        {nightly ?? rate.totalLabel}
        {!rate.nightlyLabel && rate.nightlyPriceLabel ? rate.nightlyUnitLabel : null}
      </span>
      {secondary ? (
        <span className="text-caption text-muted-foreground tabular-nums">{secondary}</span>
      ) : null}
    </span>
  );
}

/** Título e nome acessível da linha: o fornecedor identifica a linha em TODA
 *  caixa — logo + canal como título, plano como complemento. Na negociada o
 *  header só diz que HÁ acordo; pode haver acordos de fornecedores distintos
 *  na mesma caixa, então cada linha diz o seu, e o nome acessível ganha o
 *  prefixo `Negociada ·` para o contexto não se perder fora da moldura. */
function rateLineText(
  rate: HotelRateView,
  group: RateGroup,
  copy: HotelRoomsCopy,
): { title: string; complement?: string; imageUrl?: string; accessibleLabel: string } {
  if (group.negotiated) {
    const title = rate.channelLabel ?? rate.planLabel ?? group.label;
    const accessibleLabel =
      [group.label, rate.channelLabel, rate.planLabel].filter(Boolean).join(' · ') || group.label;
    return {
      title,
      ...(rate.channelLabel && rate.planLabel ? { complement: rate.planLabel } : {}),
      ...(rate.channelImageUrl ? { imageUrl: rate.channelImageUrl } : {}),
      accessibleLabel,
    };
  }
  const title = rate.channelLabel ?? copy.otherRatesLabel;
  return {
    title,
    ...(rate.planLabel ? { complement: rate.planLabel } : {}),
    ...(rate.channelImageUrl ? { imageUrl: rate.channelImageUrl } : {}),
    accessibleLabel: rate.planLabel ? `${title} · ${rate.planLabel}` : title,
  };
}

function RateLine({
  rate,
  copy,
  group,
  withCancellation,
  disabled,
}: {
  readonly rate: HotelRateView;
  readonly copy: HotelRoomsCopy;
  readonly group: RateGroup;
  /** Só quando a garantia NÃO subiu pro topo do painel. */
  readonly withCancellation: boolean;
  readonly disabled: boolean;
}) {
  const labelId = useId();
  const { title, complement, imageUrl, accessibleLabel } = rateLineText(rate, group, copy);
  const logo = useImageFallback(imageUrl);
  /* A linha segue o rádio: sem isto, clicar numa tarifa indisponível promovia
     a tarifa pro card com o rádio recusando a marcação — dois estados na tela. */
  const locked = disabled || !rate.available;
  return (
    /* A linha inteira clica porque ELA É o `<label>` do rádio: a delegação é
       nativa, então mouse e teclado entram pelo mesmo `RadioGroupItem` — e
       tarifa travada recusa o clique sozinha, sem guarda em JS. Um `onClick` de
       linha era um segundo canal de seleção, só do mouse. */
    /* `@max-md:flex-wrap`: no card estreito (o `@container` é o card) o preço
       desce pra própria linha (`basis-full` em `RateLinePrice`), em vez de
       espremer quem vende até "Boo…" — o card mede menos de 28rem no celular. */
    <label
      className={`flex items-center gap-3 rounded-md p-2 text-left transition-colors @max-md:flex-wrap ${
        locked ? 'opacity-50' : 'hover:bg-muted/40 cursor-pointer'
      }`}
    >
      {/* Envolver o rádio faz a linha inteira nomeá-lo (benefícios, prazo,
          preço), então o nome curto vem de um alvo explícito — `aria-label`
          perde do `<label>` na composição do nome. */}
      <RadioGroupItem value={rate.id} aria-labelledby={labelId} disabled={locked} />
      <span id={labelId} className="sr-only">
        {accessibleLabel}
      </span>
      {/* Duas linhas: quem vende (ou o plano, na negociada) em cima, o que a
          tarifa entrega embaixo — altura igual em toda linha, que é o que deixa
          a lista comparável. */}
      <span className="grid min-w-0 flex-1 gap-1.5">
        <span className="text-ui flex min-w-0 items-center gap-1.5">
          {logo.src ? (
            <img
              src={logo.src}
              alt=""
              aria-hidden
              onError={logo.onError}
              className="size-4 shrink-0 rounded-[4px] object-contain"
            />
          ) : null}
          {/* Quem vende não encolhe: com título e complemento truncando na
              mesma linha flex, o layout estreito espremia os DOIS e "VOLL"/
              "Booking" viravam "V…"/"Boo…" — a informação diferenciadora era a
              primeira a sumir. O título segura (com teto pra nome patológico)
              e só o complemento trunca; café/reembolso seguem nos chips. */}
          <span className="max-w-[60%] shrink-0 truncate">{title}</span>
          {complement ? (
            <span className="text-muted-foreground min-w-0 truncate">{complement}</span>
          ) : null}
        </span>
        {rate.sourceName ? (
          <span className="text-caption text-muted-foreground min-w-0 truncate">
            {rate.sourceName}
          </span>
        ) : null}
        <span className="text-caption flex min-w-0 flex-wrap items-center gap-1">
          <RateBenefitChips rate={rate} copy={copy} />
          {/* Fora da política no MESMO container dos benefícios (`CHIP`), não
              `Badge`: numa linha só, duas tags de alturas diferentes liam como
              dois níveis de aviso. */}
          {rate.policyTone === 'outside' && rate.policyLabel ? (
            <span className={`${CHIP} ${CHIP_TONE.absent} tabular-nums`}>
              <ShieldOff className="size-3 shrink-0" aria-hidden />
              {rate.policyLabel}
            </span>
          ) : null}
        </span>
        {/* O prazo de cancelamento é FRASE, não chip: na mesma forma da garantia
            içada ao topo do painel. Como chip (`whitespace-nowrap`) uma frase
            estourava a caixa na horizontal no card de 342px do celular, e
            truncar esconderia justamente a data-limite. */}
        {withCancellation && rate.cancellationLabel ? (
          <span className="text-caption text-muted-foreground">{rate.cancellationLabel}</span>
        ) : null}
      </span>
      <RateLinePrice rate={rate} />
    </label>
  );
}

/** O header da caixa é palavra pura (o TIPO, ou "Negociada") — o logo do
 *  fornecedor vive na LINHA, porque a caixa pode misturar fornecedores. */
function RateTypeHeader({ group }: { readonly group: RateGroup }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="text-ui min-w-0 truncate">{group.label}</span>
    </span>
  );
}

function RateGroupBox({
  group,
  copy,
  chosenRateId,
  withCancellation,
  disabled,
}: {
  readonly group: RateGroup;
  readonly copy: HotelRoomsCopy;
  readonly chosenRateId: string;
  readonly withCancellation: boolean;
  readonly disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const chosen = group.rates.find((rate) => rate.id === chosenRateId);
  /* Aberto, a ordem é a da caixa; fechado, quem fica de fora do expand é a
     ESCOLHIDA — colapsar depois de escolher a segunda tarifa escondia o que a
     pessoa acabou de decidir. */
  const featured = open ? group.rates[0] : (chosen ?? group.rates[0]);
  if (!featured) return null;
  const rest = group.rates.filter((rate) => rate.id !== featured.id);
  return (
    /* A borda escura marca a caixa que contém a escolha. */
    <div
      className={`relative grid rounded-lg border transition-colors ${
        chosen ? 'border-foreground' : 'border-border'
      }`}
    >
      <div className="grid gap-1 p-2.5">
        <div className="px-2 pt-1">
          <RateTypeHeader group={group} />
        </div>

        <RateLine
          rate={featured}
          copy={copy}
          group={group}
          withCancellation={withCancellation}
          disabled={disabled}
        />

        {rest.length > 0 ? (
          <Collapsible open={open}>
            <CollapsibleContent className="panel-collapse">
              <div className="grid gap-1">
                {rest.map((rate) => (
                  <RateLine
                    key={rate.id}
                    rate={rate}
                    copy={copy}
                    group={group}
                    withCancellation={withCancellation}
                    disabled={disabled}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ) : null}
      </div>

      {/* A saia da CAIXA, sem fundo: uma faixa preenchida aqui pesaria mais que
          o conteúdo que ela carrega. Ghost, e só aqui — a moldura da caixa já é
          o apoio do botão. */}
      {rest.length > 0 ? (
        <div className="border-border flex items-center justify-end border-t px-1.5 py-1">
          <Button
            variant="ghost"
            size="xs"
            className="shrink-0"
            aria-expanded={open}
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            {open ? copy.seeLessRatesLabel : copy.seeMoreRatesLabel(rest.length)}
            <ChevronDown
              className={`transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function HotelRoomRates({
  rates,
  copy,
  chosenRateId,
  disabled,
  onRatePreview,
}: {
  /** Todas as tarifas visíveis do quarto, de todos os canais. É este array que
   *  decide o painel inteiro: tipo sem tarifa não vira caixa, caixa com uma só
   *  não ganha `Ver mais`. */
  readonly rates: readonly HotelRateView[];
  readonly copy: HotelRoomsCopy;
  /** Qual linha está marcada — a tarifa que o card mostra fechado. */
  readonly chosenRateId: string;
  readonly disabled: boolean;
  readonly onRatePreview: (rateId: string) => void;
}) {
  const groups = groupRates(rates, copy);
  const cancellation = hoistedCancellation(rates);
  return (
    /* `border-t`: o corte que separa o card aberto do painel. `gap-3` entre as
       CAIXAS, `gap-1` entre as linhas de dentro — o vão maior separa um tipo
       do outro. */
    <RadioGroup
      value={chosenRateId}
      onValueChange={(id) => {
        if (typeof id === 'string' && !disabled) onRatePreview(id);
      }}
      aria-label={copy.ratesAriaLabel}
      className="border-border grid gap-3 border-t p-3.5"
    >
      {/* Dita uma vez, no lugar onde a garantia vale pra tudo que está embaixo. */}
      {cancellation ? <p className="text-caption text-muted-foreground">{cancellation}</p> : null}

      {groups.map((group, index) => {
        const opensOtherRates = !group.negotiated && groups[index - 1]?.negotiated === true;
        const containsChosen = group.rates.some((rate) => rate.id === chosenRateId);
        return (
          <section
            key={group.key}
            className="grid gap-2"
            aria-label={group.label}
            aria-current={containsChosen || undefined}
          >
            {/* A divisória entra UMA vez, antes da primeira caixa de tipo: sem
                ela a negociada virava só mais uma linha no meio das outras. */}
            {opensOtherRates ? (
              <div className="text-caption text-muted-foreground mt-1 flex items-center gap-2 font-medium">
                {copy.otherRatesLabel}
                <span className="bg-border h-px flex-1" />
              </div>
            ) : null}
            <RateGroupBox
              group={group}
              copy={copy}
              chosenRateId={chosenRateId}
              withCancellation={cancellation === undefined}
              disabled={disabled}
            />
          </section>
        );
      })}
    </RadioGroup>
  );
}
